/**
 * Checklist Data Hook
 *
 * Takes categories from useAuditDetail and provides filtering, search,
 * grouping, sorting, and computed stats for the checklist tab.
 */

import { useMemo, useState } from "react";
import type { ChecklistFindingsSummary } from "@/components/audits/tabs/checklist/checklist-findings-map";
import type {
  UIAuditCategory,
  UIChecklistItem,
  ChecklistFilterType,
} from "@/packages/types";
import type { ActiveFilter, SortState } from "@/components/ui";
import { DEFAULT_CHECKLIST_SORT } from "@/components/audits/tabs/checklist/checklist-filter-data";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface ChecklistStats {
  total: number;
  approved: number;
  uploaded: number;
  missing: number;
  progress: number;
}

export interface ChecklistGroupEntry {
  category: UIAuditCategory;
  items: UIChecklistItem[];
}

/* ------------------------------------------------------------------ */
/* Filter logic                                                        */
/* ------------------------------------------------------------------ */

function filterChecklistItems(
  items: UIChecklistItem[],
  filterType: ChecklistFilterType,
): UIChecklistItem[] {
  switch (filterType) {
    case "all":
      return items;
    case "open":
      return items.filter((item) => !item.completedAt);
    case "completed":
      return items.filter((item) => !!item.completedAt);
  }
}

/* ------------------------------------------------------------------ */
/* Search logic                                                        */
/* ------------------------------------------------------------------ */

function searchItems(
  items: UIChecklistItem[],
  query: string,
  categories: UIAuditCategory[],
): UIChecklistItem[] {
  if (!query.trim()) return items;
  const lower = query.toLowerCase();

  // Build a map of item ID → category name for searching
  const itemToCategoryName = new Map<string, string>();
  for (const cat of categories) {
    for (const item of cat.items) {
      itemToCategoryName.set(item.id, cat.name);
    }
  }

  return items.filter((item) => {
    const name = item.name?.toLowerCase() ?? "";
    const desc = item.description?.toLowerCase() ?? "";
    const catName = itemToCategoryName.get(item.id)?.toLowerCase() ?? "";
    const clauseRefs =
      item.clauseDetails?.map((c) => c.clauseNumber.toLowerCase()).join(" ") ??
      "";

    return (
      name.includes(lower) ||
      desc.includes(lower) ||
      catName.includes(lower) ||
      clauseRefs.includes(lower)
    );
  });
}

/* ------------------------------------------------------------------ */
/* Advanced filter logic                                               */
/* ------------------------------------------------------------------ */

const STATUS_ORDER: Record<string, number> = {
  APPROVED: 0,
  UPLOADED: 1,
  MISSING: 2,
};

function applyAdvancedFilters(
  items: UIChecklistItem[],
  activeFilters: ActiveFilter[],
): UIChecklistItem[] {
  if (activeFilters.length === 0) return items;

  return items.filter((item) => {
    // AND logic: every filter must match
    return activeFilters.every((filter) => {
      if (filter.categoryId === "status") {
        const itemStatus = item.status.toLowerCase();
        if (filter.operator === "is" || filter.operator === "is_either_of") {
          return filter.valueIds.includes(itemStatus);
        }
        if (filter.operator === "is_not") {
          return !filter.valueIds.includes(itemStatus);
        }
        return true;
      }

      if (filter.categoryId === "required") {
        const requiredValue = item.required ? "yes" : "no";
        if (filter.operator === "is") {
          return filter.valueIds.includes(requiredValue);
        }
        return true;
      }

      return true;
    });
  });
}

function applyToggleFilters(
  items: UIChecklistItem[],
  hasEvidenceFilter: boolean,
  hasFindingsFilter: boolean,
  findingsMap: Map<UIChecklistItem["id"], ChecklistFindingsSummary>,
): UIChecklistItem[] {
  let result = items;

  if (hasEvidenceFilter) {
    result = result.filter((item) => (item.linkedDocumentIds?.length ?? 0) > 0);
  }

  if (hasFindingsFilter) {
    result = result.filter((item) => findingsMap.has(item.id));
  }

  return result;
}

/* ------------------------------------------------------------------ */
/* Sort logic                                                          */
/* ------------------------------------------------------------------ */

function sortChecklistItems(
  items: UIChecklistItem[],
  sortState: SortState,
): UIChecklistItem[] {
  const sorted = [...items];
  const { field, direction } = sortState;
  const multiplier = direction === "asc" ? 1 : -1;

  sorted.sort((a, b) => {
    // Primary sort: non-annex items first, annex items second
    const annexA = a.isAnnex ? 1 : 0;
    const annexB = b.isAnnex ? 1 : 0;
    if (annexA !== annexB) return annexA - annexB;

    // Secondary sort: apply user-selected field sorting within each group
    switch (field) {
      case "name": {
        const nameA = a.name?.toLowerCase() ?? "";
        const nameB = b.name?.toLowerCase() ?? "";
        return nameA.localeCompare(nameB) * multiplier;
      }
      case "status": {
        const orderA = STATUS_ORDER[a.status] ?? 99;
        const orderB = STATUS_ORDER[b.status] ?? 99;
        return (orderA - orderB) * multiplier;
      }
      case "dueDate": {
        const dateA = a.dueDate ?? Number.MAX_SAFE_INTEGER;
        const dateB = b.dueDate ?? Number.MAX_SAFE_INTEGER;
        return (dateA - dateB) * multiplier;
      }
      case "required": {
        // required first when asc
        const reqA = a.required ? 0 : 1;
        const reqB = b.required ? 0 : 1;
        return (reqA - reqB) * multiplier;
      }
      default:
        return 0;
    }
  });

  return sorted;
}

/* ------------------------------------------------------------------ */
/* Grouping                                                            */
/* ------------------------------------------------------------------ */

function groupChecklistByCategory(
  items: UIChecklistItem[],
  categories: UIAuditCategory[],
): Map<string, ChecklistGroupEntry> {
  const itemIds = new Set(items.map((i) => i.id));
  const grouped = new Map<string, ChecklistGroupEntry>();

  for (const category of categories) {
    const matching = category.items.filter((item) => itemIds.has(item.id));
    if (matching.length > 0) {
      grouped.set(category.id, { category, items: matching });
    }
  }

  return grouped;
}

/* ------------------------------------------------------------------ */
/* Stats                                                               */
/* ------------------------------------------------------------------ */

function computeStats(items: UIChecklistItem[]): ChecklistStats {
  let approved = 0;
  let uploaded = 0;
  let missing = 0;

  for (const item of items) {
    // Exclude soaExcluded items from progress calculations
    if (item.soaExcluded) continue;
    switch (item.status) {
      case "APPROVED":
        approved++;
        break;
      case "UPLOADED":
        uploaded++;
        break;
      case "MISSING":
        missing++;
        break;
    }
  }

  const total = approved + uploaded + missing;
  const progress = total > 0 ? Math.round((approved / total) * 100) : 0;

  return { total, approved, uploaded, missing, progress };
}

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */

interface UseChecklistDataReturn {
  filteredItems: UIChecklistItem[];
  visualOrderItems: UIChecklistItem[];
  grouped: Map<string, ChecklistGroupEntry>;
  stats: ChecklistStats;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  activeFilter: ChecklistFilterType;
  setActiveFilter: (f: ChecklistFilterType) => void;
  sortState: SortState;
  setSortState: (s: SortState) => void;
  hasEvidenceFilter: boolean;
  setHasEvidenceFilter: (v: boolean) => void;
  hasFindingsFilter: boolean;
  setHasFindingsFilter: (v: boolean) => void;
  hasEvidenceCount: number;
  hasFindingsCount: number;
}

const EMPTY_FINDINGS_MAP = new Map<
  UIChecklistItem["id"],
  ChecklistFindingsSummary
>();

export function useChecklistData(
  categories: UIAuditCategory[] | undefined,
  activeFilters: ActiveFilter[] = [],
  findingsMap: Map<
    UIChecklistItem["id"],
    ChecklistFindingsSummary
  > = EMPTY_FINDINGS_MAP,
): UseChecklistDataReturn {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<ChecklistFilterType>("all");
  const [sortState, setSortState] = useState<SortState>(DEFAULT_CHECKLIST_SORT);
  const [hasEvidenceFilter, setHasEvidenceFilter] = useState(false);
  const [hasFindingsFilter, setHasFindingsFilter] = useState(false);

  const result = useMemo(() => {
    if (!categories || categories.length === 0) {
      const emptyGrouped = new Map<string, ChecklistGroupEntry>();
      return {
        filteredItems: [] as UIChecklistItem[],
        visualOrderItems: [] as UIChecklistItem[],
        grouped: emptyGrouped,
        stats: { total: 0, approved: 0, uploaded: 0, missing: 0, progress: 0 },
        hasEvidenceCount: 0,
        hasFindingsCount: 0,
      };
    }

    // Flatten all items from categories
    const allItems = categories.flatMap((cat) => cat.items);

    const hasEvidenceCount = allItems.filter(
      (item) => (item.linkedDocumentIds?.length ?? 0) > 0,
    ).length;
    const hasFindingsCount = allItems.filter((item) =>
      findingsMap.has(item.id),
    ).length;

    // Apply quick filter chips
    const filtered = filterChecklistItems(allItems, activeFilter);

    // Apply text search
    const searched = searchItems(filtered, searchQuery, categories);

    // Apply advanced filters from Filter context
    const advancedFiltered = applyAdvancedFilters(searched, activeFilters);

    const toggleFiltered = applyToggleFilters(
      advancedFiltered,
      hasEvidenceFilter,
      hasFindingsFilter,
      findingsMap,
    );

    // Sort
    const sorted = sortChecklistItems(toggleFiltered, sortState);

    // Group by category
    const grouped = groupChecklistByCategory(sorted, categories);

    // Flatten grouped entries in category order for side sheet navigation
    const visualOrderItems: UIChecklistItem[] = [];
    for (const [, group] of grouped) {
      visualOrderItems.push(...group.items);
    }

    // Compute stats from all items (not filtered)
    const stats = computeStats(allItems);

    return {
      filteredItems: sorted,
      visualOrderItems,
      grouped,
      stats,
      hasEvidenceCount,
      hasFindingsCount,
    };
  }, [
    categories,
    activeFilter,
    searchQuery,
    activeFilters,
    sortState,
    hasEvidenceFilter,
    hasFindingsFilter,
    findingsMap,
  ]);

  return {
    ...result,
    searchQuery,
    setSearchQuery,
    activeFilter,
    setActiveFilter,
    sortState,
    setSortState,
    hasEvidenceFilter,
    setHasEvidenceFilter,
    hasFindingsFilter,
    setHasFindingsFilter,
    hasEvidenceCount: result.hasEvidenceCount,
    hasFindingsCount: result.hasFindingsCount,
  };
}
